<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveCommunityDetailStore, liveManagedEventsStore } from '../../lib/convexRealtime';
  import type { ManagedEventConfig } from '../../lib/convexApi';
  import type { CommunityDetail } from '../../lib/types';
  import { session, openSignIn } from '../session';
  import { gotoCommunity } from '../router';
  import { accentStyle, formatEventDate } from '../format';
  import ApiKeysPanel from '../components/ApiKeysPanel.svelte';
  import MembersPanel from '../components/MembersPanel.svelte';
  import CommunitySettingsPanel from '../components/CommunitySettingsPanel.svelte';
  import EventApplicationsPanel from '../components/EventApplicationsPanel.svelte';
  import EventEditorPanel from '../components/EventEditorPanel.svelte';
  import EventGuestsPanel from '../components/EventGuestsPanel.svelte';
  import EventInventoryPanel from '../components/EventInventoryPanel.svelte';
  import EventOrdersPanel from '../components/EventOrdersPanel.svelte';
  import InviteManagerPanel from '../components/InviteManagerPanel.svelte';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let slug: string;

  type Tab = 'events' | 'guests' | 'invites' | 'applications' | 'members' | 'orders' | 'settings';
  const TABS: Array<{ id: Tab; label: string }> = [
    { id: 'events', label: 'Events' },
    { id: 'guests', label: 'Guests' },
    { id: 'invites', label: 'Invites' },
    { id: 'applications', label: 'Applications' },
    { id: 'members', label: 'Members' },
    { id: 'orders', label: 'Orders' },
    { id: 'settings', label: 'Settings' }
  ];

  function initialTab(): Tab {
    const raw = new URLSearchParams(window.location.search).get('tab');
    return TABS.some(tab => tab.id === raw) ? (raw as Tab) : 'events';
  }

  let activeTab: Tab = initialTab();

  function selectTab(tab: Tab) {
    activeTab = tab;
    const params = new URLSearchParams(window.location.search);
    if (tab === 'events') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const query = params.toString();
    window.history.replaceState({}, '', query ? `${window.location.pathname}?${query}` : window.location.pathname);
  }

  let detailStore: ReturnType<typeof liveCommunityDetailStore> | null = null;
  let detailUnsub: (() => void) | null = null;
  let subscribedSlug: string | null = null;

  let community: CommunityDetail | null = null;
  let loading = true;
  let error: string | null = null;

  let eventsStore: ReturnType<typeof liveManagedEventsStore> | null = null;
  let eventsUnsub: (() => void) | null = null;
  let subscribedCommunityId: string | null = null;

  let managedEvents: ManagedEventConfig[] = [];
  let eventsLoading = true;
  let eventsError: string | null = null;

  let selectedEventId = '';
  let editorEvent: ManagedEventConfig | null = null;
  let showEditor = false;

  $: if (slug && slug !== subscribedSlug) {
    subscribedSlug = slug;
    detailUnsub?.();
    detailStore?.dispose();
    detailStore = liveCommunityDetailStore(slug);
    detailUnsub = detailStore.subscribe(state => {
      community = state.data ?? null;
      loading = state.loading;
      error = state.error;
    });
  }

  $: if (community?.isManager && community.id && community.id !== subscribedCommunityId) {
    subscribedCommunityId = community.id;
    eventsUnsub?.();
    eventsStore?.dispose();
    eventsStore = liveManagedEventsStore(community.id);
    eventsUnsub = eventsStore.subscribe(state => {
      managedEvents = state.data ?? [];
      eventsLoading = state.loading;
      eventsError = state.error;
    });
  }

  $: if (managedEvents.length > 0 && !managedEvents.some(event => event.id === selectedEventId)) {
    selectedEventId = managedEvents[0].id;
  }

  $: selectedEvent = managedEvents.find(event => event.id === selectedEventId) ?? null;
  $: applyEvents = managedEvents.filter(event => event.overrides.ticketAccess === 'apply');

  onDestroy(() => {
    detailUnsub?.();
    detailStore?.dispose();
    eventsUnsub?.();
    eventsStore?.dispose();
  });

  function openCreate() {
    editorEvent = null;
    showEditor = true;
  }

  function openEdit(event: ManagedEventConfig) {
    editorEvent = event;
    showEditor = true;
  }

  async function onEditorSaved() {
    showEditor = false;
    editorEvent = null;
    await eventsStore?.refresh();
    await detailStore?.refresh();
  }

  async function onEditorRefresh() {
    await eventsStore?.refresh();
    await detailStore?.refresh();
  }

  function tierSummary(event: ManagedEventConfig): string {
    const tiers = event.ticketing.tiers;
    if (tiers.length === 0) {
      const price = event.ticket.startPrice;
      return price > 0 ? `Single ticket · from ${price}` : 'Free / invite ticket';
    }
    return `${tiers.length} tier${tiers.length === 1 ? '' : 's'}`;
  }
</script>

<div class="manage container themed" style={accentStyle(community?.themeColor)}>
  {#if loading && !community}
    <LoadingBlock label="Loading…" />
  {:else if error}
    <Notice variant="error">Could not load the community: {error}</Notice>
  {:else if !community}
    <Notice variant="error">This community does not exist.</Notice>
  {:else if !$session.signedIn}
    <div class="manage__gate">
      <Notice>Sign in with a manager account to open the community admin.</Notice>
      <button type="button" class="accent" on:click={() => openSignIn()}>Sign in</button>
    </div>
  {:else if !community.isManager}
    <Notice variant="error">You don't manage {community.name}. Ask an owner to promote you.</Notice>
  {:else}
    <header class="manage__header">
      <div>
        <button type="button" class="manage__back" on:click={() => gotoCommunity(slug)}>
          ← {community.name}
        </button>
        <h1>Community admin</h1>
      </div>
    </header>

    <nav class="manage__tabs" aria-label="Admin sections">
      {#each TABS as tab (tab.id)}
        <button
          type="button"
          class="manage__tab"
          class:manage__tab--active={activeTab === tab.id}
          on:click={() => selectTab(tab.id)}
        >
          {tab.label}
        </button>
      {/each}
    </nav>

    {#if activeTab === 'events'}
      <section class="manage__section">
        <div class="manage__section-head">
          <h2>Events</h2>
          <button type="button" class="accent" on:click={openCreate}>Create event</button>
        </div>

        {#if showEditor}
          <EventEditorPanel
            communityId={community.id}
            event={editorEvent}
            on:saved={onEditorSaved}
            on:refresh={onEditorRefresh}
            on:cancel={() => (showEditor = false)}
          />
        {/if}

        {#if eventsError}
          <Notice variant="error">Could not load events: {eventsError}</Notice>
        {:else if eventsLoading && managedEvents.length === 0}
          <LoadingBlock label="Loading events…" />
        {:else if managedEvents.length === 0}
          <p class="manage__empty">No events yet — create the first one.</p>
        {:else}
          <ul class="manage__events">
            {#each managedEvents as event (event.id)}
              <li class="manage__event surface surface--raised">
                <div class="manage__event-info">
                  <span class="manage__event-name">{event.name}</span>
                  <span class="manage__event-meta">
                    {formatEventDate(event.schedule) ?? 'No date'}
                    {#if event.location?.name}
                      · {event.location.name}
                    {/if}
                    · {tierSummary(event)}
                  </span>
                  <span class="manage__event-meta">
                    Listing: {event.overrides.visibilityOverride} · Tickets: {event.overrides.ticketAccess}
                    {#if event.ticketing.capacity != null}
                      · Cap {event.ticketing.capacity}
                    {/if}
                  </span>
                </div>
                <div class="manage__event-actions">
                  <button type="button" class="secondary" on:click={() => openEdit(event)}>
                    Edit & tickets
                  </button>
                  <button
                    type="button"
                    class="ghost"
                    on:click={() => {
                      selectedEventId = event.id;
                      selectTab('guests');
                    }}
                  >
                    Guests
                  </button>
                </div>
              </li>
            {/each}
          </ul>
          <p class="manage__hint">
            "Edit & tickets" holds everything about an event — details, extras, ticket tiers,
            capacity and sale windows. Access rules (listing, who can get tickets) live in Settings.
          </p>
        {/if}
      </section>
    {:else if activeTab === 'members'}
      <section class="manage__section">
        <MembersPanel
          communityId={community.id}
          isOwner={community.membership?.role === 'owner'}
          communityName={community.name}
          communitySlug={community.slug}
          welcomeMessage={community.welcomeMessage ?? null}
        />
      </section>
    {:else if activeTab === 'applications'}
      <section class="manage__section">
        {#if applyEvents.length === 0}
          <Notice>
            No events use per-event applications. This mode is no longer configurable — gate access by
            community membership instead. Any legacy apply-gated events still surface their queue here.
          </Notice>
        {:else}
          {#each applyEvents as event (event.id)}
            <EventApplicationsPanel eventId={event.id} eventName={event.name} />
          {/each}
        {/if}
      </section>
    {:else if activeTab === 'settings'}
      <section class="manage__section">
        <CommunitySettingsPanel {community} on:changed={() => void detailStore?.refresh()} />
        <ApiKeysPanel
          communityId={community.id}
          isOwner={community.membership?.role === 'owner' || $session.isAdmin}
        />
      </section>
    {:else}
      <section class="manage__section">
        {#if managedEvents.length === 0}
          <p class="manage__empty">Create an event first.</p>
        {:else}
          <label class="manage__event-select">
            Event
            <select bind:value={selectedEventId}>
              {#each managedEvents as event (event.id)}
                <option value={event.id}>{event.name}</option>
              {/each}
            </select>
          </label>

          {#if selectedEvent}
            {#if activeTab === 'guests'}
              <EventGuestsPanel eventId={selectedEvent.id} eventName={selectedEvent.name} />
            {:else if activeTab === 'invites'}
              <InviteManagerPanel eventId={selectedEvent.id} eventName={selectedEvent.name} />
            {:else if activeTab === 'orders'}
              <EventOrdersPanel eventId={selectedEvent.id} eventName={selectedEvent.name} />
              <EventInventoryPanel eventId={selectedEvent.id} eventName={selectedEvent.name} />
            {/if}
          {/if}
        {/if}
      </section>
    {/if}
  {/if}
</div>

<style>
  .manage {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-6) var(--space-4) var(--space-8);
    max-width: var(--content-max-width);
  }

  .manage__gate {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    align-items: flex-start;
  }

  .manage__header h1 {
    margin: var(--space-2) 0 0;
    font-size: var(--font-size-xl);
  }

  .manage__back {
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    cursor: pointer;
    padding: 0;
  }

  .manage__back:hover {
    color: var(--color-text-primary);
  }

  .manage__tabs {
    display: flex;
    gap: var(--space-1);
    flex-wrap: wrap;
    border-bottom: var(--border-subtle);
    padding-bottom: var(--space-2);
  }

  .manage__tab {
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius-pill);
    border: 1px solid transparent;
    background: transparent;
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .manage__tab--active {
    color: var(--color-text-primary);
    border-color: rgb(var(--accent-rgb) / 0.5);
    background: rgb(var(--accent-rgb) / 0.08);
  }

  .manage__section {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .manage__section-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .manage__section-head h2 {
    margin: 0;
    font-size: var(--font-size-lg);
  }

  .manage__events {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .manage__event {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-lg);
    flex-wrap: wrap;
  }

  .manage__event-info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .manage__event-name {
    font-weight: 600;
    font-size: var(--font-size-md);
  }

  .manage__event-meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .manage__event-actions {
    display: flex;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .manage__event-select {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    max-width: 320px;
  }

  .manage__event-select select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .manage__hint {
    margin: 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .manage__empty {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }
</style>
